/**
 * Frontend API client pro product variants
 * Načítá varianty z Supabase přes Workers API
 */

import { createApiUrl, fetchWithTimeout } from './api';

export interface ProductVariant {
  id: string;
  shopify_variant_id: string;
  shopify_product_id: string | null;
  size: string;
  frame_type: string;
  frame_color: string;
  display_name: string;
  description: string | null;
  price_cents: number | null;
  currency: string;
  is_active: boolean;
  is_available: boolean;
  sku: string | null;
  barcode: string | null;
  inventory: {
    quantity: number;
    available_quantity: number;
    inventory_management: string | null;
    inventory_policy: string;
  } | null;
  last_synced_at: string | null;
  sync_status: string;
}

export interface VariantsResponse {
  variants: ProductVariant[];
  count: number;
}

export interface VariantResponse {
  variant: ProductVariant | null;
}

/**
 * Získá konkrétní variant podle atributů
 * 
 * @param size - Velikost produktu (např. 'B2', '50x70')
 * @param frameType - Typ rámu ('frame', 'hanger', 'none')
 * @param frameColor - Barva rámu ('oak', 'white', 'black', 'none')
 * @returns ProductVariant nebo null pokud není nalezen
 */
export async function getVariant(
  size: string,
  frameType: string,
  frameColor: string = 'oak'
): Promise<ProductVariant | null> {
  try {
    const params = new URLSearchParams({
      size,
      frame_type: frameType,
      frame_color: frameColor,
    });

    const url = createApiUrl(`/api/variants/find?${params.toString()}`);
    const response = await fetchWithTimeout(url);

    if (!response.ok) {
      if (response.status === 404) {
        // Variant není nalezen - to je OK, použijeme fallback
        return null;
      }
      throw new Error(`Failed to fetch variant: ${response.status}`);
    }

    const data: VariantResponse = await response.json();
    return data.variant;

  } catch (error) {
    console.warn('[getVariant] Failed to fetch from API, will use fallback:', error);
    return null;
  }
}

/**
 * Získá všechny dostupné varianty s volitelným filtrováním
 * 
 * @param filters - Volitelné filtry
 * @returns Pole variant
 */
export async function getAllVariants(filters?: {
  size?: string;
  frameType?: string;
  frameColor?: string;
  productType?: 'poster' | 'frame' | 'hanger';
  onlyAvailable?: boolean;
}): Promise<ProductVariant[]> {
  try {
    const params = new URLSearchParams();

    if (filters?.size) {
      params.set('size', filters.size);
    }
    if (filters?.frameType) {
      params.set('frame_type', filters.frameType);
    }
    if (filters?.frameColor) {
      params.set('frame_color', filters.frameColor);
    }
    if (filters?.productType) {
      params.set('product_type', filters.productType);
    }
    if (filters?.onlyAvailable) {
      params.set('only_available', 'true');
    }

    const queryString = params.toString();
    const baseUrl = createApiUrl('/api/variants');
    const url = queryString
      ? `${baseUrl}?${queryString}`
      : baseUrl;

    const response = await fetchWithTimeout(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch variants: ${response.status}`);
    }

    const data: VariantsResponse = await response.json();
    // console.log('[getAllVariants] RAW API RESPONSE:', JSON.stringify(data, null, 2));

    // PATCH: Fix potential DB data issues where price is stored as 39 cents instead of 3990
    // This is a temporary fix until the DB is corrected.
    let variants = data.variants || [];

    // Debug: Pokud jsme nenašli žádné varianty, zkusíme zjistit, zda existují ale jsou neaktivní
    // Toto pomůže při debugování "nekonečného processingu" v košíku
    if (variants.length === 0 && !filters?.onlyAvailable) {
      // Zkusíme načíst znova s include_inactive=true pro diagnostiku
      // DŮLEŽITÉ: Musíme odstranit only_available filtr, abychom našli i nedostupné varianty
      const debugUrl = `${baseUrl}?include_inactive=true${filters?.size ? `&size=${filters.size}` : ''}${filters?.frameType ? `&frame_type=${filters.frameType}` : ''}${filters?.frameColor ? `&frame_color=${filters.frameColor}` : ''}`;
      try {
        const debugResponse = await fetchWithTimeout(debugUrl);
        if (debugResponse.ok) {
          const debugData: VariantsResponse = await debugResponse.json();
          if (debugData.variants && debugData.variants.length > 0) {
            // console.warn(`[getAllVariants] ⚠️ Found ${debugData.variants.length} inactive variants! Sync likely marked them as inactive. Check Shopify product status.`);
            // Volitelně můžeme vrátit neaktivní varianty, pokud opravdu chceme
            // Ale prozatím jen logujeme varování
          } else {
            console.warn(`[getAllVariants] ⚠️ No variants found even with include_inactive=true. Database is likely empty.`);
          }
        }
      } catch (e) {
        console.warn('[getAllVariants] Debug fetch failed', e);
      }
    }

    const patchedVariants = variants.map(v => {
      // Pokud je cena rámu/hangeru podezřele nízká (např. 39 centů -> 0.39 EUR), opravíme ji
      // 500 cents = 5 EUR. Rám stojí spíše 39 EUR.
      if (['frame', 'hanger'].includes(v.frame_type) && v.price_cents !== null && v.price_cents < 1000 && v.price_cents > 0) {
        // console.warn(`[getAllVariants] Patching suspicious price for ${v.frame_type}: ${v.price_cents} -> ${v.price_cents * 100}`);
        return { ...v, price_cents: v.price_cents * 100 };
      }
      return v;
    });

    return patchedVariants || [];

  } catch (error) {
    console.error('[getAllVariants] Error:', error);
    return [];
  }
}


/**
 * Získá Shopify variant ID pro danou konfiguraci
 * 
 * @param size - Velikost produktu
 * @param frameType - Typ rámu
 * @param frameColor - Barva rámu
 * @returns Shopify variant ID nebo null
 */
export async function getShopifyVariantId(
  size: string,
  frameType: string,
  frameColor: string = 'oak'
): Promise<string | null> {
  const variant = await getVariant(size, frameType, frameColor);
  return variant?.shopify_variant_id || null;
}

/**
 * Získá Shopify variant ID pro poster (samostatný produkt s variantami pouze pro velikosti)
 * 
 * @param size - Velikost posteru (např. 'B2', '50x70', 'A3')
 * @returns Shopify variant ID nebo null pokud není nalezen
 */
export async function getPosterVariantId(
  size: string
): Promise<string | null> {
  const variant = await getVariant(size, 'none', 'none');
  return variant?.shopify_variant_id || null;
}

/**
 * Získá Shopify variant ID pro frame (samostatný produkt pro upsell)
 * 
 * @param posterSize - Velikost posteru (např. 'B2', '50x70') - určuje který frame produkt
 * @param frameColor - Barva frame ('oak', 'white', 'black')
 * @returns Shopify variant ID nebo null pokud není nalezen
 */
export async function getFrameVariantId(
  posterSize: string,
  frameColor: string = 'oak'
): Promise<string | null> {
  // 1. Zkusit s původní velikostí
  let variant = await getVariant(posterSize, 'frame', frameColor);
  if (variant?.shopify_variant_id) return variant.shopify_variant_id;

  // 2. Pokud se nenajde, zkusit alternativní formát (B2 <-> 50x70)
  const altSize = posterSize === 'B2' ? '50x70' : (posterSize === '50x70' ? 'B2' : null);

  if (altSize) {
    // console.log(`[getFrameVariantId] Frame not found for ${posterSize}, trying ${altSize}...`);
    variant = await getVariant(altSize, 'frame', frameColor);
    if (variant?.shopify_variant_id) return variant.shopify_variant_id;
  }

  return null;
}

/**
 * Získá Shopify variant ID pro hanger (samostatný produkt pro upsell)
 * 
 * @param posterSize - Velikost posteru (např. 'B2', '50x70') - určuje který hanger produkt
 * @param frameColor - Barva hangeru ('oak', 'white', 'black')
 * @returns Shopify variant ID nebo null pokud není nalezen
 */
export async function getHangerVariantId(
  posterSize: string,
  frameColor: string = 'oak'
): Promise<string | null> {
  // 1. Zkusit s původní velikostí
  let variant = await getVariant(posterSize, 'hanger', frameColor);
  if (variant?.shopify_variant_id) return variant.shopify_variant_id;

  // 2. Fallback pro A2 -> B2 (hanger je kompatibilní/akceptovatelný)
  if (posterSize === 'A2') {
    // Zkusit B2 nebo 50x70
    variant = await getVariant('B2', 'hanger', frameColor);
    if (!variant) {
      variant = await getVariant('50x70', 'hanger', frameColor);
    }
    if (variant?.shopify_variant_id) return variant.shopify_variant_id;
  }

  return variant?.shopify_variant_id || null;
}

